import { toKebabCase } from './capitalize';

function getSlug(slug?: string) {
  if (!slug) {
    return '';
  }
  if (slug.startsWith('/')) {
    slug = slug.slice(1);
  }
  if (slug.endsWith('/')) {
    slug = slug.slice(0, -1);
  }
  return slug;
}

export const TEMPLATES_BASE_PATH = '/templates/';
export const PRICING_BASE_PATH = '/pricing/';
export const isPricingUrl = (pathname: string) => pathname.startsWith(PRICING_BASE_PATH);

export function isTemplatesUrl(pathname: string) {
  return pathname.startsWith(TEMPLATES_BASE_PATH);
}
export function getSectionUrl(slug?: string) {
  return `${TEMPLATES_BASE_PATH}${getSlug(slug)}/`;
}
export function getCategoryUrl(parentSlug: string, slug: string) {
  return `${getSectionUrl(parentSlug)}${getSlug(slug)}/`;
}
export function getSinglePageUrl(slug: string) {
  return `${TEMPLATES_BASE_PATH}s/${getSlug(slug)}/`;
}

export function buildTemplateUrl(hash: string, name?: string, category?: string) {
  const url = basicTemplateUrl(hash, name);
  return category ? `${url}${toKebabCase(category)}/` : url;
}

function basicTemplateUrl(hash: string, name?: string) {
  if (!name) {
    return `${TEMPLATES_BASE_PATH}t/${hash}/`;
  }

  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '-');
  const encodedSlug = encodeURIComponent(slug);

  return `${TEMPLATES_BASE_PATH}t/${hash}-${encodedSlug}/`;
}

/**
 * Creates an absolute URL from a relative path.
 * @param url - The relative URL path
 * @param websiteDomain - The base domain (e.g., "https://example.com/")
 */
export function absoluteUrl(url: string, websiteDomain: string) {
  return new URL(url, websiteDomain).toString();
}

const INTERNAL_DOMAINS = ['thebrief.ai', 'thebrief.local', 'thebrief.dev'];

export function isExternalUrl(url?: string | null) {
  // An absolute URL is external unless its HOST is an internal domain or one of
  // its subdomains; the domain may still appear in an external URL's path or query
  // (e.g. https://www.instagram.com/thebrief.ai).
  if (!url?.startsWith('http')) {
    return false;
  }

  try {
    const { hostname } = new URL(url);
    return !INTERNAL_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

export function getUrlWithTrailingSlash(url?: string | null) {
  if (!url) {
    return '';
  }

  // Handle simple URLs without query params or fragments
  const hasQueryParams = url.includes('?');
  const hasFragment = url.includes('#');

  if (!hasQueryParams && !hasFragment) {
    return url.endsWith('/') ? url : `${url}/`;
  }

  // For complex URLs, we need to add the trailing slash to the path part only
  // Example: "/blog?param=1" becomes "/blog/?param=1"
  //          "/blog#section" becomes "/blog/#section"

  const queryStart = url.indexOf('?');
  const fragmentStart = url.indexOf('#');

  // Find where the path ends (first occurrence of ? or #)
  let pathEndIndex = url.length;
  if (queryStart !== -1) {
    pathEndIndex = Math.min(pathEndIndex, queryStart);
  }
  if (fragmentStart !== -1) {
    pathEndIndex = Math.min(pathEndIndex, fragmentStart);
  }

  const pathPart = url.substring(0, pathEndIndex);
  const restOfUrl = url.substring(pathEndIndex);

  // Add trailing slash to path if it doesn't have one
  const pathWithSlash = pathPart.endsWith('/') ? pathPart : `${pathPart}/`;

  return pathWithSlash + restOfUrl;
}

/**
 * Gets the domain without trailing slash.
 * @param websiteDomain - The full domain URL (e.g., "https://example.com/")
 */
export function getDomain(websiteDomain: string): string {
  return websiteDomain.endsWith('/') ? websiteDomain.slice(0, -1) : websiteDomain;
}

/**
 * Gets the templates URL.
 * @param websiteDomain - The full domain URL
 */
export function getTemplatesUrl(websiteDomain: string): string {
  return `${getDomain(websiteDomain)}/templates`;
}

/**
 * Gets the blog URL.
 * @param websiteDomain - The full domain URL
 */
export function getBlogUrl(websiteDomain: string): string {
  return `${getDomain(websiteDomain)}/blog`;
}
